import { useMutation, useQueryClient } from 'react-query'
import { useAuth0 } from '../../../contexts/auth0-context'
import callAPI from '../../../API/callSecureAPI'
import endpoints from '../../../API/endpoints'
import { TppCell, TppColumn, TppRow, TppSection, Tpp, TppSlot } from '../types'

export const useTppMutations = (tppId: string | null | undefined) => {
    const { getTokenSilently } = useAuth0()
    const queryClient = useQueryClient()

    const callTppEndpoint = async <T>(
        endpoint: { url: string; method: string },
        path?: string,
        body?: Record<string, any>
    ): Promise<T> => {
        const token = await getTokenSilently({ audience: 'https://atlas.aesara.com' })
        const rawUrl = path ? `${endpoint.url}/${path}` : endpoint.url
        const url = rawUrl.includes(':tppId') ? rawUrl.replace(':tppId', tppId) : rawUrl
        const response = await callAPI(url, endpoint.method, token, body)
        return response.data as T
    }

    // --- TPP ---

    const updateAssetAmbition = useMutation<Tpp, unknown, { assetAmbition: string }>(
        async ({ assetAmbition }: { assetAmbition: string }) => {
            return callTppEndpoint<Tpp>(endpoints.updateAssetAmbition, undefined, {
                assetAmbition,
            })
        },
        {
            onSuccess: (updatedTpp) => {
                queryClient.setQueryData<Tpp>(['tpp', tppId], (old) => {
                    if (!old) return updatedTpp
                    return { ...old, assetAmbition: updatedTpp.assetAmbition }
                })
            },
        }
    )

    // --- COLUMNS ---
    const reorderColumns = useMutation<Tpp, unknown, string[], { previousTpp: Tpp | undefined }>(
        async (newOrder: string[]) => {
            return callTppEndpoint<Tpp>(endpoints.reorderTPPColumns, `${tppId}/column/reorder`, { columnIds: newOrder })
        },
        {
            onSuccess: (updatedTpp) => {
                const nextColumnIds = updatedTpp?.columnIds || []
                queryClient.setQueryData<Tpp>(['tpp', tppId], (old) => {
                    if (!old) return updatedTpp
                    return { ...old, columnIds: nextColumnIds.length ? nextColumnIds : old.columnIds }
                })
            },
        }
    )

    const togglePinColumn = useMutation<TppColumn, unknown, string, { previousColumns: TppColumn[] | undefined }>(
        async (columnId: string) => {
            return callTppEndpoint<TppColumn>(endpoints.togglePinTPPColumn, `${columnId}/pin`)
        },
        {
            onSuccess: (updatedColumn) => {
                queryClient.setQueryData<TppColumn[]>(['tppColumns', tppId], (old) => {
                    if (!old) return []
                    return old.map((c) => (c._id === updatedColumn._id ? updatedColumn : c))
                })
            },
        }
    )

    const toggleLockColumn = useMutation<TppColumn, unknown, string, { previousColumns: TppColumn[] | undefined }>(
        async (columnId: string) => {
            return callTppEndpoint<TppColumn>(endpoints.toggleLockTPPColumn, `${columnId}/lock`)
        },
        {
            onSuccess: (updatedColumn) => {
                queryClient.setQueryData<TppColumn[]>(['tppColumns', tppId], (old) => {
                    if (!old) return []
                    return old.map((c) => (c._id === updatedColumn._id ? updatedColumn : c))
                })
            },
        }
    )

    const toggleArchiveColumn = useMutation<TppColumn, unknown, string, { previousColumns: TppColumn[] | undefined }>(
        async (columnId: string) => {
            return callTppEndpoint<TppColumn>(endpoints.archiveTPPColumn, `${columnId}/archive`, { archivedNote: '' })
        },
        {
            onSuccess: (updatedColumn) => {
                queryClient.setQueryData<TppColumn[]>(['tppColumns', tppId], (old) => {
                    if (!old) return []
                    return old.map((c) => (c._id === updatedColumn._id ? updatedColumn : c))
                })
            },
        }
    )

    // From HEAD - createColumnVersion mutation for version history
    const createColumnVersion = useMutation<
        { _id: string; versionNumber: number; columnTitle: string; createdAt: string; note?: string },
        unknown,
        { columnId: string; note?: string }
    >(
        async ({ columnId, note }) => {
            return callTppEndpoint(endpoints.createTPPColumnVersion, `${columnId}/versions`, { note })
        },
        {
            onSuccess: (result, { columnId }) => {
                // Invalidate the version history query so it refetches with the new version
                queryClient.invalidateQueries(['tppColumnVersions', columnId])
            },
            onError: (err) => {
                console.error('❌ Version creation failed:', err)
            },
        }
    )

    // --- SECTIONS ---
    const reorderSections = useMutation<Tpp, unknown, string[], { previousTpp: Tpp | undefined }>(
        async (newOrder: string[]) => {
            return callTppEndpoint<Tpp>(endpoints.reorderTPPSections, `${tppId}/section/reorder`, {
                sectionIds: newOrder,
            })
        },
        {
            onMutate: async (newOrder) => {
                await queryClient.cancelQueries(['tpp', tppId])
                const previousTpp = queryClient.getQueryData<Tpp>(['tpp', tppId])

                if (previousTpp) {
                    queryClient.setQueryData<Tpp>(['tpp', tppId], { ...previousTpp, sectionIds: newOrder })
                }
                return { previousTpp }
            },
            onError: (_err, _vars, context) => {
                if (context?.previousTpp) {
                    queryClient.setQueryData(['tpp', tppId], context.previousTpp)
                }
            },
            onSuccess: (updatedTpp) => {
                const nextSectionIds = updatedTpp?.sectionIds || []
                queryClient.setQueryData<Tpp>(['tpp', tppId], (old) => {
                    if (!old) return updatedTpp
                    return { ...old, sectionIds: nextSectionIds.length ? nextSectionIds : old.sectionIds }
                })
            },
        }
    )

    const createSection = useMutation<{ section: TppSection; tpp: Tpp }, unknown, { title: string }>(
        async ({ title }) => {
            return callTppEndpoint(endpoints.createTPPSection, undefined, { tppId, title })
        },
        {
            onSuccess: (data) => {
                if (!data) return
                const { section, tpp } = data

                queryClient.setQueryData<TppSection[]>(['tppSections', tppId], (old) => {
                    if (!old) return [section]
                    if (old.some((s) => s._id === section._id)) return old
                    return [...old, section]
                })

                queryClient.setQueryData<Tpp>(['tpp', tppId], (old) => {
                    if (!old) return tpp
                    return { ...old, sectionIds: tpp.sectionIds }
                })
            },
        }
    )

    const updateSectionTitle = useMutation<TppSection, unknown, { sectionId: string; title: string }>(
        async ({ sectionId, title }) => {
            return callTppEndpoint<TppSection>(endpoints.updateTPPSectionTitle, `${sectionId}/title`, { title })
        },
        {
            onSuccess: (updatedSection) => {
                queryClient.setQueryData<TppSection[]>(['tppSections', tppId], (old) => {
                    if (!old) return []
                    return old.map((s) => (s._id === updatedSection._id ? updatedSection : s))
                })
            },
        }
    )

    const deleteSection = useMutation<
        { section: TppSection; tpp: Tpp; deletedRows: string[]; deletedSlots: string[]; deletedCells: string[] },
        unknown,
        string
    >(
        async (sectionId: string) => {
            return callTppEndpoint(endpoints.deleteTPPSection, sectionId)
        },
        {
            onSuccess: (data, sectionId) => {
                if (!data) return
                const { tpp, deletedRows, deletedSlots, deletedCells } = data

                queryClient.setQueryData<TppSection[]>(['tppSections', tppId], (old) => {
                    if (!old) return []
                    return old.filter((s) => s._id !== sectionId)
                })

                queryClient.setQueryData<Tpp>(['tpp', tppId], (old) => {
                    if (!old) return tpp
                    return { ...old, sectionIds: tpp.sectionIds }
                })

                queryClient.setQueryData<TppRow[]>(['tppRows', tppId], (old) => {
                    if (!old) return []
                    return old.filter((r) => !deletedRows.includes(r._id))
                })

                queryClient.setQueryData<TppSlot[]>(['tppSlots', tppId], (old) => {
                    if (!old) return []
                    return old.filter((s) => !deletedSlots.includes(s._id))
                })

                queryClient.setQueryData<TppCell[]>(['tppCells', tppId], (old) => {
                    if (!old) return []
                    return old.filter((c) => !deletedCells.includes(c._id))
                })
            },
        }
    )

    // --- ROWS ---
    const reorderRows = useMutation<
        TppSection,
        unknown,
        { sectionId: string; newOrder: string[] },
        { previousSections: TppSection[] | undefined }
    >(
        async ({ sectionId, newOrder }: { sectionId: string; newOrder: string[] }) => {
            return callTppEndpoint<TppSection>(endpoints.reorderTPPRows, `${sectionId}/row/reorder`, {
                rowIds: newOrder,
            })
        },
        {
            onMutate: async ({ sectionId, newOrder }) => {
                await queryClient.cancelQueries(['tppSections', tppId])
                const previousSections = queryClient.getQueryData<TppSection[]>(['tppSections', tppId])

                if (previousSections) {
                    queryClient.setQueryData<TppSection[]>(
                        ['tppSections', tppId],
                        previousSections.map((s) => (s._id === sectionId ? { ...s, rowIds: newOrder } : s))
                    )
                }
                return { previousSections }
            },
            onError: (_err, _vars, context) => {
                if (context?.previousSections) {
                    queryClient.setQueryData(['tppSections', tppId], context.previousSections)
                }
            },
            onSuccess: (updatedSection, vars) => {
                queryClient.setQueryData<TppSection[]>(['tppSections', tppId], (old) => {
                    if (!old) return []
                    const nextRowIds = Array.isArray(updatedSection)
                        ? updatedSection
                        : updatedSection?.rowIds || vars.newOrder
                    return old.map((s) => (s._id === vars.sectionId ? { ...s, rowIds: nextRowIds } : s))
                })
            },
        }
    )

    const createRow = useMutation<
        { row: TppRow; slot: TppSlot; cell: TppCell; section: TppSection },
        unknown,
        { sectionId: string; title: string }
    >(
        async ({ sectionId, title }) => {
            return callTppEndpoint(endpoints.createTPPRow, undefined, { sectionId, tppId, title })
        },
        {
            onSuccess: (data) => {
                if (!data) return
                const { row, slot, cell, section } = data

                queryClient.setQueryData<TppRow[]>(['tppRows', tppId], (old) => {
                    if (!old) return [row]
                    if (old.some((r) => r._id === row._id)) return old
                    return [...old, row]
                })

                queryClient.setQueryData<TppSlot[]>(['tppSlots', tppId], (old) => {
                    if (!old) return [slot]
                    if (old.some((s) => s._id === slot._id)) return old
                    return [...old, slot]
                })

                if (cell) {
                    queryClient.setQueryData<TppCell[]>(['tppCells', tppId], (old) => {
                        if (!old) return [cell]
                        if (old.some((c) => c._id === cell._id)) return old
                        return [...old, cell]
                    })
                }

                queryClient.setQueryData<TppSection[]>(['tppSections', tppId], (old) => {
                    if (!old) return [section]
                    return old.map((s) => (s._id === section._id ? section : s))
                })
            },
        }
    )

    const updateRowTitle = useMutation<TppRow, unknown, { rowId: string; title: string }>(
        async ({ rowId, title }) => {
            return callTppEndpoint<TppRow>(endpoints.updateTPPRowTitle, `${rowId}/title`, { title })
        },
        {
            onSuccess: (updatedRow) => {
                queryClient.setQueryData<TppRow[]>(['tppRows', tppId], (old) => {
                    if (!old) return []
                    return old.map((r) => (r._id === updatedRow._id ? updatedRow : r))
                })
            },
        }
    )

    const deleteRow = useMutation<
        { row: TppRow; section: TppSection; deletedSlots: string[]; deletedCells: string[] },
        unknown,
        string
    >(
        async (rowId: string) => {
            return callTppEndpoint(endpoints.deleteTPPRow, rowId)
        },
        {
            onSuccess: (data, rowId) => {
                if (!data) return
                const { section, deletedSlots, deletedCells } = data

                queryClient.setQueryData<TppRow[]>(['tppRows', tppId], (old) => {
                    if (!old) return []
                    return old.filter((r) => r._id !== rowId)
                })

                queryClient.setQueryData<TppSection[]>(['tppSections', tppId], (old) => {
                    if (!old) return [section]
                    return old.map((s) => (s._id === section._id ? section : s))
                })

                queryClient.setQueryData<TppSlot[]>(['tppSlots', tppId], (old) => {
                    if (!old) return []
                    return old.filter((s) => !deletedSlots.includes(s._id))
                })

                queryClient.setQueryData<TppCell[]>(['tppCells', tppId], (old) => {
                    if (!old) return []
                    return old.filter((c) => !deletedCells.includes(c._id))
                })
            },
        }
    )

    // --- SLOTS ---
    const reorderSlots = useMutation<
        TppRow,
        unknown,
        { rowId: string; newOrder: string[] },
        { previousRows: TppRow[] | undefined }
    >(
        async ({ rowId, newOrder }: { rowId: string; newOrder: string[] }) => {
            return callTppEndpoint<TppRow>(endpoints.reorderTPPSlots, `${rowId}/slot/reorder`, { slots: newOrder })
        },
        {
            onMutate: async ({ rowId, newOrder }) => {
                await queryClient.cancelQueries(['tppRows', tppId])
                const previousRows = queryClient.getQueryData<TppRow[]>(['tppRows', tppId])

                if (previousRows) {
                    queryClient.setQueryData<TppRow[]>(
                        ['tppRows', tppId],
                        previousRows.map((r) => (r._id === rowId ? { ...r, slotIds: newOrder } : r))
                    )
                }
                return { previousRows }
            },
            onError: (err, vars, context) => {
                if (context?.previousRows) {
                    queryClient.setQueryData(['tppRows', tppId], context.previousRows)
                }
            },
            onSuccess: (updatedRow, vars) => {
                queryClient.setQueryData<TppRow[]>(['tppRows', tppId], (old) => {
                    if (!old) return []
                    return old.map((r) => (r._id === vars.rowId ? updatedRow : r))
                })
            },
        }
    )

    const createSlot = useMutation<
        { slot: TppSlot; cell: TppCell; row: TppRow },
        unknown,
        { rowId: string },
        {
            previousRows: TppRow[] | undefined
            previousSlots: TppSlot[] | undefined
            previousCells: TppCell[] | undefined
        }
    >(
        async ({ rowId }: { rowId: string }) => {
            return callTppEndpoint(endpoints.createTPPSlot, undefined, { rowId, tppId })
        },
        {
            onMutate: async () => {
                await Promise.all([
                    queryClient.cancelQueries(['tppRows', tppId]),
                    queryClient.cancelQueries(['tppSlots', tppId]),
                    queryClient.cancelQueries(['tppCells', tppId]),
                ])

                const previousRows = queryClient.getQueryData<TppRow[]>(['tppRows', tppId])
                const previousSlots = queryClient.getQueryData<TppSlot[]>(['tppSlots', tppId])
                const previousCells = queryClient.getQueryData<TppCell[]>(['tppCells', tppId])

                return { previousRows, previousSlots, previousCells }
            },
            onError: (_err, _vars, context) => {
                if (context?.previousRows) {
                    queryClient.setQueryData(['tppRows', tppId], context.previousRows)
                }
                if (context?.previousSlots) {
                    queryClient.setQueryData(['tppSlots', tppId], context.previousSlots)
                }
                if (context?.previousCells) {
                    queryClient.setQueryData(['tppCells', tppId], context.previousCells)
                }
            },
            onSuccess: (data) => {
                if (!data) return
                const { slot, cell, row } = data

                queryClient.setQueryData<TppRow[]>(['tppRows', tppId], (old) => {
                    if (!old) return old
                    return old.map((r) => (r._id === row._id ? row : r))
                })

                queryClient.setQueryData<TppSlot[]>(['tppSlots', tppId], (old) => {
                    if (!old) return [slot]
                    if (old.some((s) => s._id === slot._id)) return old
                    return [...old, slot]
                })

                queryClient.setQueryData<TppCell[]>(['tppCells', tppId], (old) => {
                    if (!old) return [cell]
                    if (old.some((c) => c._id === cell._id)) return old
                    return [...old, cell]
                })
            },
        }
    )

    const updateSlotContext = useMutation<
        TppSlot,
        unknown,
        { slotId: string; context: string },
        { previousSlots: TppSlot[] | undefined }
    >(
        async ({ slotId, context }) => {
            return callTppEndpoint<TppSlot>(endpoints.updateTPPSlotContext, `${slotId}/context/update`, { context })
        },
        {
            onMutate: async ({ slotId, context }) => {
                await queryClient.cancelQueries(['tppSlots', tppId])
                const previousSlots = queryClient.getQueryData<TppSlot[]>(['tppSlots', tppId])

                if (previousSlots) {
                    queryClient.setQueryData<TppSlot[]>(
                        ['tppSlots', tppId],
                        previousSlots.map((slot) => (slot._id === slotId ? { ...slot, context } : slot))
                    )
                }

                return { previousSlots }
            },
            onError: (_err, _vars, context) => {
                if (context?.previousSlots) {
                    queryClient.setQueryData(['tppSlots', tppId], context.previousSlots)
                }
            },
            onSuccess: (updatedSlot) => {
                queryClient.setQueryData<TppSlot[]>(['tppSlots', tppId], (old) => {
                    if (!old) return [updatedSlot]
                    return old.map((slot) => (slot._id === updatedSlot._id ? updatedSlot : slot))
                })
            },
        }
    )

    const createCell = useMutation<TppCell, unknown, { slotId: string; columnId: string }>(
        async ({ slotId, columnId }) => {
            return callTppEndpoint<TppCell>(endpoints.createTPPCell, undefined, {
                tppId,
                slotId,
                columnId,
                content: 'Click here to edit',
            })
        },
        {
            onSuccess: (newCell) => {
                if (!newCell) return
                queryClient.setQueryData<TppCell[]>(['tppCells', tppId], (old) => {
                    if (!old) return [newCell]
                    if (old.some((c) => c._id === newCell._id)) return old
                    return [...old, newCell]
                })
            },
        }
    )

    // --- CELLS ---
    const moveCell = useMutation<
        { cell: TppCell; row?: TppRow },
        unknown,
        {
            cellId: string
            sourceSlotId: string
            sourceColumnId: string
            targetSlotId: string
            targetColumnId: string
        },
        {
            previousCells: TppCell[] | undefined
            previousRows: TppRow[] | undefined
            previousSlots: TppSlot[] | undefined
        }
    >(
        async ({
            cellId,
            targetSlotId,
            targetColumnId,
        }: {
            cellId: string
            sourceSlotId: string
            sourceColumnId: string
            targetSlotId: string
            targetColumnId: string
            targetCellId?: string
        }) => {
            return callTppEndpoint(endpoints.reorderTPPCell, `${cellId}/reorder`, {
                newSlotId: targetSlotId,
                newColumnId: targetColumnId,
            })
        },
        {
            onMutate: async ({ cellId, targetSlotId, targetColumnId }) => {
                await Promise.all([
                    queryClient.cancelQueries(['tppRows', tppId]),
                    queryClient.cancelQueries(['tppSlots', tppId]),
                    queryClient.cancelQueries(['tppCells', tppId]),
                ])

                const previousRows = queryClient.getQueryData<TppRow[]>(['tppRows', tppId])
                const previousSlots = queryClient.getQueryData<TppSlot[]>(['tppSlots', tppId])
                const previousCells = queryClient.getQueryData<TppCell[]>(['tppCells', tppId])

                if (previousCells) {
                    queryClient.setQueryData<TppCell[]>(
                        ['tppCells', tppId],
                        previousCells.map((c) => {
                            if (c._id === cellId) {
                                return { ...c, slotId: targetSlotId, columnId: targetColumnId }
                            }
                            return c
                        })
                    )
                }

                return { previousRows, previousSlots, previousCells }
            },
            onError: (err, vars, context) => {
                if (context?.previousCells) {
                    queryClient.setQueryData(['tppCells', tppId], context.previousCells)
                }
            },
            onSuccess: (data, vars) => {
                const { cell, row } = data

                queryClient.setQueryData<TppCell[]>(['tppCells', tppId], (old) => {
                    if (!old) return []
                    return old.map((c) => (c._id === cell._id ? { ...c, ...cell } : c))
                })

                if (row) {
                    queryClient.setQueryData<TppRow[]>(['tppRows', tppId], (old) => {
                        if (!old) return []
                        return old.map((r) => (r._id === row._id ? row : r))
                    })
                    queryClient.setQueryData<TppSlot[]>(['tppSlots', tppId], (old) => {
                        if (!old) return []
                        return old.filter((s) => s._id !== vars.sourceSlotId)
                    })
                }
            },
        }
    )

    return {
        updateAssetAmbition,
        reorderColumns,
        reorderSections,
        reorderRows,
        reorderSlots,
        moveCell,
        togglePinColumn,
        toggleLockColumn,
        toggleArchiveColumn,
        createSlot,
        updateSlotContext,
        createCell,
        createColumnVersion,
        // Section CRUD
        createSection,
        updateSectionTitle,
        deleteSection,
        // Row CRUD
        createRow,
        updateRowTitle,
        deleteRow,
    }
}
